import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import { ApiUrls } from '@api/apiUrls';
import { fetchApi } from '@api/fetchApi';
import { EnumUserRoute } from '@components-member/userRoutes/enums';
import { Modal } from '@components-shared/modal/Modal';
import { ButtonPreloader } from '@components-shared/preloader';
import { keyMap } from '@locales/lokalise/keyMap';
import { handleError } from '@utils/errors';
import { getDateTimeWithOffset, getUtcTime, isAfterDateTime } from '@utils/library/dateTime';
import cn from 'classnames';
import { IRouteComponentProps } from 'global.interfaces';
import { observer } from 'mobx-react';
import { useMobxStore } from 'store/mobx';
import styles from './styles.module.scss';

const UserJurisdictionAcceptModal = (props: IRouteComponentProps): JSX.Element | null => {
  const { i18n } = useTranslation();
  const { history } = props;
  const { accountStore } = useMobxStore();

  const {
    userInfo: { jurisdictionAgreementAcceptedOn, jurisdictionAgreementReadOn, jurisdictionAgreementAcceptWithinDays },
    generalSettings: {
      jurisdictionSettings: { popup },
    },
  } = accountStore;

  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [accepting, setAccepting] = useState<boolean>(false);

  useEffect(() => {
    if (!jurisdictionAgreementAcceptedOn) {
      setPopupOpen(true);
    }
  }, [jurisdictionAgreementAcceptedOn]);

  const currentTimeUtc = getUtcTime();

  const timeAfterDaysUtc = jurisdictionAgreementReadOn
    ? getDateTimeWithOffset(jurisdictionAgreementReadOn, jurisdictionAgreementAcceptWithinDays, 'd')
    : '';

  const isPopupBlocked = isAfterDateTime(currentTimeUtc, timeAfterDaysUtc);

  const acceptAgreement = async () => {
    try {
      setAccepting(true);

      await fetchApi.post(ApiUrls.jurisdictionUser.acceptAgreement);

      setAccepting(true);
      setPopupOpen(false);
    } catch (error) {
      handleError(keyMap.sharedErrorsFailedToSaveRecord, error);
      setAccepting(false);
    }
  };

  const openHubspotChat = () => {
    const hubSpotChat = window.HubSpotConversations;
    const isHubspotChatLoaded = hubSpotChat?.widget.status().loaded;

    if (hubSpotChat && isHubspotChatLoaded) {
      hubSpotChat.widget.open();
    }

    if (hubSpotChat && !isHubspotChatLoaded && isPopupBlocked) {
      return;
    }

    if (!isPopupBlocked && ((hubSpotChat && !isHubspotChatLoaded) || !hubSpotChat)) {
      history.push(EnumUserRoute.ticketPage);
    }

    if (!isPopupBlocked) {
      togglePopup();
    }
  };

  const togglePopup = () => {
    setPopupOpen(!popupOpen);
  };

  if (!popupOpen) {
    return null;
  }

  const popupConfig = popup.find((i) => i.language === i18n.language);

  return (
    <Modal title={popupConfig?.title} showModalCloseButton={false} closeHandler={togglePopup} closeOnEscKey={false}>
      <p>{popupConfig?.body}</p>
      <Trans i18nKey={keyMap.jurisdictionPopupReadDocuments}>
        <p>
          Click
          <a
            rel="noreferrer"
            target="_blank"
            className={cn('link-button', styles.jurisdictionPopup__accent)}
            href={popupConfig?.link}></a>
          to view and read the updated documents
        </p>
      </Trans>
      <div className={styles.jurisdictionPopup__buttonWrapper}>
        <button onClick={acceptAgreement} className="btn">
          {accepting ? <ButtonPreloader /> : i18n.t(keyMap.jurisdictionPopupAcceptButton)}
        </button>
      </div>
      <p>
        {i18n.t(keyMap.jurisdictionPopupQuestions)}
        <span onClick={openHubspotChat} className={cn('link-button', styles.jurisdictionPopup__accent)}>
          {i18n.t(keyMap.jurisdictionPopupChatButton)}
        </span>
      </p>
    </Modal>
  );
};

export const UserJurisdictionPopupModalWithRouter = withRouter(observer(UserJurisdictionAcceptModal));
